import React from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { shouldSkipSummarySelector } from '../AdvWorkflow/AdvWorkflowSelector';
import { PersonalInfoWorkflow } from './steps/PersonalInfoWorkflow/PersonalInfoWorkflow';
import TaxesWorkflow from '../TaxesWorkflow/TaxesWorkflowContainer';
import { AccountsWorkflow } from './steps/AccountsWorkflow/AccountsWorkflow';
import YearlyDistribution from './steps/YearlyDistribution/YearlyDistributionContainer';
import DistributionYears from './steps/DistributionYears/DistributionYearsContainer';
import AdvTopLevelWorkflow from '../AdvWorkflow/AdvTopLevelWorkflowContainer';
import {
  endOfWorkflowsRedirectTo,
  reloadAdvizrApplication,
} from '../AdvWorkflow/AdvWorkflowHelpers';
import { getGoalPageName } from '../../FinancialPlanOPL/Goals/utils/getGoalPageName';
import { GoalTypeDTO } from '../../FinancialPlanOPL/types/goalsDTO';
import { isUuid } from '../../../js/app/react/validators';
import { WORKFLOW_ID } from './DecumulationWorkflow.constants';
import { useFeatureFlag } from '../../_hooks/useFeatureFlag';
import { Feature } from '../../CommonSelectors/CommonArea.types';

interface Props {
  clientId: string;
  goToSummaryWhenReady: string; // route param: '0' or '1'
}

export const DecumulationWorkflow = ({ clientId, goToSummaryWhenReady, ...rest }: Props) => {
  const shouldSkipSummary = useSelector(state =>
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    shouldSkipSummarySelector(state, { clientId, goToSummaryWhenReady }),
  );

  const isDecumulationFeatureFlag = useFeatureFlag(Feature.DecumulationProposalAccountWf);

  const outputFunction = response => {
    const { ADV } = window;
    reloadAdvizrApplication(true, false, () => {
      const currentClient = ADV.util.getCurrentClient(ADV.data);

      const outputRoute = currentClient.ComponentName || getGoalPageName(GoalTypeDTO.Decumulation);

      const goalId = get(response, 'payload.viewData.Goal.Id', null);

      if (!isUuid(goalId)) {
        throw new Error("'payload.viewData.Goal.Id' must be an uuid.");
      }

      const params = {
        clientId,
        goalId,
      };

      endOfWorkflowsRedirectTo(outputRoute, params);
    });
  };

  return (
    <AdvTopLevelWorkflow
      {...rest}
      clientId={clientId}
      hasAutoOutput={shouldSkipSummary}
      goToSummaryWhenReady={goToSummaryWhenReady}
      workflowId={WORKFLOW_ID}
      analyticsWorkflowStarted="decumulation_workflow_started"
      outputFunction={outputFunction}
      steps={[
        PersonalInfoWorkflow,
        TaxesWorkflow,
        // As other workflow type is of function, pass an empty function that returns null in array instead of empty array
        ...(isDecumulationFeatureFlag ? [() => null] : [AccountsWorkflow]),
        YearlyDistribution,
        DistributionYears,
      ]}
    />
  );
};
